import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL  = import.meta.env.VITE_SUPABASE_URL  || '';
const SUPABASE_KEY  = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

// Supabase client — will be null if env vars not set (uses localStorage fallback)
export const supabase = SUPABASE_URL && SUPABASE_KEY
  ? createClient(SUPABASE_URL, SUPABASE_KEY, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
      },
      realtime: {
        params: { eventsPerSecond: 10 },
      },
    })
  : null;

// ─── Auth helpers ─────────────────────────────────────────────
export const auth = {
  signIn: (email, password) =>
    supabase?.auth.signInWithPassword({ email, password }),

  signUp: (email, password, metadata = {}) =>
    supabase?.auth.signUp({ email, password, options: { data: metadata } }),

  signOut: () => supabase?.auth.signOut(),

  getUser: () => supabase?.auth.getUser(),

  onAuthStateChange: (callback) =>
    supabase?.auth.onAuthStateChange(callback),
};

// ─── Database helpers ─────────────────────────────────────────
// These wrap Supabase calls. When Supabase isn't configured,
// they fall through to the localStorage state managed by AppContext.

export const db = {
  // ── Multi-user: company + membership ────────────────────────
  company: {
    getForUser: async (userId) => {
      if (!supabase) return { data: null };
      const { data: member } = await supabase
        .from('account_members')
        .select('company_id, role, company:companies(*)')
        .eq('user_id', userId)
        .order('created_at', { ascending: true })
        .limit(1)
        .maybeSingle();
      return { data: member };
    },
    get:    (id)        => supabase?.from('companies').select('*').eq('id', id).single(),
    update: (id, data)  => supabase?.from('companies').update(data).eq('id', id),
    create: (data)      => supabase?.from('companies').insert(data).select().single(),
  },
  members: {
    list:   (companyId) => supabase?.from('account_members').select('*').eq('company_id', companyId),
    create: (data)      => supabase?.from('account_members').insert(data).select().single(),
    delete: (userId, companyId) =>
      supabase?.from('account_members').delete().eq('user_id', userId).eq('company_id', companyId),
  },

  customers: {
    list:   (companyId) => supabase?.from('customers').select('*').eq('company_id', companyId),
    get:    (id)        => supabase?.from('customers').select('*').eq('id', id).single(),
    create: (data)      => supabase?.from('customers').insert(data).select().single(),
    update: (id, data)  => supabase?.from('customers').update(data).eq('id', id),
    delete: (id)        => supabase?.from('customers').delete().eq('id', id),
  },
  stops: {
    list:   (date)     => supabase?.from('stops').select('*, customer:customers(*)').eq('date', date),
    update: (id, data) => supabase?.from('stops').update(data).eq('id', id),
    create: (data)     => supabase?.from('stops').insert(data).select().single(),
  },
  invoices: {
    list:   (companyId) => supabase?.from('invoices').select('*').eq('company_id', companyId).order('date', { ascending: false }),
    create: (data)      => supabase?.from('invoices').insert(data).select().single(),
    update: (id, data)  => supabase?.from('invoices').update(data).eq('id', id),
  },
  plugins: {
    get:    (companyId) => supabase?.from('account_plugins').select('*').eq('company_id', companyId),
    set:    (companyId, accountId, pluginId, enabled) =>
      supabase?.from('account_plugins').upsert({ company_id: companyId, account_id: accountId, plugin_id: pluginId, enabled }),
  },
  technicians: {
    list:   (companyId) => supabase?.from('technicians').select('*').eq('company_id', companyId).order('created_at', { ascending: true }),
    create: (data)      => supabase?.from('technicians').insert(data).select().single(),
    update: (id, data)  => supabase?.from('technicians').update(data).eq('id', id),
    delete: (id)        => supabase?.from('technicians').delete().eq('id', id),
  },
  messages: {
    listForCustomer: (customerId) =>
      supabase?.from('messages').select('*').eq('customer_id', customerId).order('created_at', { ascending: false }),
    listRecent: (companyId, limit = 50) =>
      supabase?.from('messages').select('*').eq('company_id', companyId).order('created_at', { ascending: false }).limit(limit),
  },
  trackingEvents: {
    create: (data) => supabase?.from('tracking_events').insert(data),
    listRecent: (companyId, limit = 50) =>
      supabase?.from('tracking_events').select('*').eq('company_id', companyId).order('created_at', { ascending: false }).limit(limit),
  },
  photos: {
    listForStop: (stopId) =>
      supabase?.from('photos').select('*').eq('stop_id', stopId).order('created_at', { ascending: true }),
    listForCustomer: (customerId) =>
      supabase?.from('photos').select('*').eq('customer_id', customerId).order('created_at', { ascending: false }).limit(50),
    create: (data) => supabase?.from('photos').insert(data).select().single(),
    delete: (id) => supabase?.from('photos').delete().eq('id', id),
  },
  estimates: {
    list:    (companyId) => supabase?.from('estimates').select('*').eq('company_id', companyId).order('created_at', { ascending: false }),
    listForCustomer: (customerId) =>
      supabase?.from('estimates').select('*').eq('customer_id', customerId).order('created_at', { ascending: false }),
    get:     (id)       => supabase?.from('estimates').select('*').eq('id', id).single(),
    create:  (data)     => supabase?.from('estimates').insert(data).select().single(),
    update:  (id, data) => supabase?.from('estimates').update(data).eq('id', id).select().single(),
  },
};

// ─── Generic Edge Function invoker ────────────────────────────
async function invokeEdge(name, body) {
  if (!supabase) return { ok: false, error: 'Cloud not configured' };
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    if (!token) return { ok: false, error: 'Not signed in' };
    const url = `${supabase.supabaseUrl}/functions/v1/${name}`;
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'apikey': supabase.supabaseKey,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body || {}),
    });
    let payload = null;
    try { payload = await res.json(); } catch {}
    if (!res.ok) return { ok: false, error: payload?.error || `HTTP ${res.status}`, data: payload };
    return { ok: true, data: payload };
  } catch (e) {
    return { ok: false, error: String(e?.message || e) };
  }
}

// ─── Edge Function: geocode-address ───────────────────────────
export async function geocodeAddress({ address, customerId }) {
  return invokeEdge('geocode-address', { address, customer_id: customerId ?? null });
}

// ─── Edge Function: optimize-route ────────────────────────────
export async function optimizeRoute({ stopIds, origin, returnToOrigin }) {
  return invokeEdge('optimize-route', {
    stop_ids: stopIds,
    origin,
    return_to_origin: returnToOrigin,
  });
}

// ─── Edge Function: subscribe-push ────────────────────────────
export async function registerPushSubscription(subscription, userAgent) {
  return invokeEdge('subscribe-push', {
    subscription: subscription.toJSON ? subscription.toJSON() : subscription,
    user_agent: userAgent || (typeof navigator !== 'undefined' ? navigator.userAgent : ''),
  });
}

// ─── Edge Function: send-push ─────────────────────────────────
// Defaults to sending to the caller's own user_id when targetUserId omitted.
export async function sendPushViaEdge({ targetUserId, title, body, url, tag }) {
  return invokeEdge('send-push', {
    user_id: targetUserId || undefined,
    title, body, url, tag,
  });
}

// ─── Edge Function: photo-upload-url ──────────────────────────
export async function requestPhotoUploadUrl({ stopId, customerId, kind, contentType }) {
  return invokeEdge('photo-upload-url', {
    stop_id: stopId, customer_id: customerId, kind, content_type: contentType,
  });
}

// ─── Edge Function: email-export ──────────────────────────────
export async function emailExport({ to, subject, body, attachments }) {
  return invokeEdge('email-export', { to, subject, body, attachments });
}

// ─── Edge Function: send-message ──────────────────────────────
// Returns: { ok, message?, error? }
export async function sendMessageViaEdge({ to, channel, subject, body, customerId }) {
  const res = await invokeEdge('send-message', {
    to, channel, subject, body, customer_id: customerId ?? null,
  });
  return {
    ok: res.ok,
    message: res.data?.message,
    error: res.error,
  };
}

// ─── Edge Function: invite-tech ───────────────────────────────
export async function inviteTechViaEdge({ email, role, appUrl }) {
  return invokeEdge('invite-tech', { email, role, app_url: appUrl });
}

// ─── Edge Function: accept-invite ─────────────────────────────
export async function acceptInviteViaEdge({ token }) {
  return invokeEdge('accept-invite', { token });
}

// ─── Edge Function: route-brief ───────────────────────────────
// Returns: { ok, briefs?, date?, error? }
// briefs: [{ tech, summary, total_stops, error? }]
export async function generateRouteBrief({ tech, date } = {}) {
  const res = await invokeEdge('route-brief', { tech, date });
  return {
    ok: res.ok,
    briefs: res.data?.briefs || [],
    date: res.data?.date,
    error: res.error,
  };
}

// ─── Real-time subscriptions ─────────────────────────────────
export const realtime = {
  // Subscribe to equipment monitoring updates (for plugin: eq_pentair, eq_hayward, etc.)
  equipmentStatus: (accountId, callback) =>
    supabase
      ?.channel(`equipment:${accountId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'equipment_status' }, callback)
      .subscribe(),

  // Subscribe to stop updates (so office sees live tech progress)
  stops: (date, callback) =>
    supabase
      ?.channel(`stops:${date}`)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'stops', filter: `date=eq.${date}` }, callback)
      .subscribe(),
};
